/**
 * Shared date-display helpers for the Clinical Trials dashboard.
 */

/**
 * Parse an ISO calendar date without treating it as UTC midnight.
 *
 * @param value - A date value in `YYYY-MM-DD` format.
 * @returns The corresponding local date, or undefined for invalid input.
 */
export function parseIsoCalendarDate(value: string): Date | undefined {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return undefined;
  }
  return date;
}

/**
 * The clinic's local timezone. Full timestamps are resolved to this zone before
 * their display date is extracted so late-evening-UTC appointments (e.g. a 2:40 PM
 * Eastern visit stored as 18:40 UTC) never roll over to the following calendar day
 * on machines running in a different local timezone.
 */
const CLINIC_TIME_ZONE = 'America/New_York';

/**
 * Detect whether a string carries a time-of-day (and therefore a timezone meaning).
 *
 * Plain calendar dates (`YYYY-MM-DD`) have no time component and must not be
 * timezone-shifted. Anything with a `T` separator, a trailing `Z`, or an explicit
 * `+HH:MM` / `-HH:MM` offset is a full timestamp.
 *
 * @param value - The raw string value to inspect.
 * @returns True when the value includes a time/zone component.
 */
function hasTimeComponent(value: string): boolean {
  return /[T ]\d{2}:\d{2}/.test(value) || /[zZ]$/.test(value) || /[+-]\d{2}:?\d{2}$/.test(value);
}

/**
 * Format a moment in the clinic timezone as `MM-DD-YYYY`.
 *
 * @param date - A valid Date instance representing a specific moment.
 * @returns The `MM-DD-YYYY` string with Y/M/D derived in `America/New_York`.
 */
function formatInClinicZone(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: CLINIC_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const lookup = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${lookup('month')}-${lookup('day')}-${lookup('year')}`;
}

/**
 * Format a date for display using the dashboard-wide `MM-DD-YYYY` convention.
 *
 * Date-only ISO strings are parsed in local time to avoid UTC date shifts. Full
 * timestamps (with a time/zone component) are resolved to the clinic timezone
 * (`America/New_York`) before their display date is extracted, so a late-UTC
 * appointment does not display a day late on non-Eastern machines. Invalid
 * non-empty strings are returned unchanged so source data remains visible.
 *
 * @param value - Date, ISO date, timestamp, or empty value to format.
 * @param emptyValue - Text returned when no date is supplied.
 * @returns The formatted date, original invalid text, or the empty fallback.
 */
export function formatDisplayDate(value?: string | Date | null, emptyValue = ''): string {
  if (!value) return emptyValue;

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const calendarDate = parseIsoCalendarDate(value);
    if (!calendarDate) return value;
    const month = String(calendarDate.getMonth() + 1).padStart(2, '0');
    const day = String(calendarDate.getDate()).padStart(2, '0');
    return `${month}-${day}-${calendarDate.getFullYear()}`;
  }

  const isTimestampString = typeof value === 'string' && hasTimeComponent(value);
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value : emptyValue;
  }

  if (isTimestampString) {
    return formatInClinicZone(date);
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}-${day}-${date.getFullYear()}`;
}
